"use client";

import { useState, useEffect } from "react";
import { FaUsers } from "react-icons/fa6";

type GameType = "minecraft" | "fivem" | "rust" | "mta" | "hytale" | "valheim";

const gameConfigs: Record<GameType, { min: number; max: number; step: number; baseRam: number; ramPerPlayer: number; name: string }> = {
    minecraft: { min: 10, max: 200, step: 5, baseRam: 2, ramPerPlayer: 0.1, name: "Minecraft" },
    fivem: { min: 10, max: 256, step: 2, baseRam: 2, ramPerPlayer: 0.02, name: "FiveM" },
    rust: { min: 50, max: 500, step: 10, baseRam: 4, ramPerPlayer: 0.05, name: "Rust" },
    mta: { min: 50, max: 1000, step: 50, baseRam: 1, ramPerPlayer: 0.005, name: "MTA" },
    hytale: { min: 10, max: 200, step: 10, baseRam: 2, ramPerPlayer: 0.1, name: "Hytale" },
    valheim: { min: 2, max: 100, step: 2, baseRam: 2, ramPerPlayer: 0.1, name: "Valheim" },
};

export default function ResourceCalculator({ 
    game,
    onPlanChange
}: { 
    game: GameType;
    onPlanChange?: (ram: number) => void;
}) {
    const config = gameConfigs[game];
    const [players, setPlayers] = useState(config.min);

    // Derived, not state: the estimate is a pure function of the slider.
    const exactRam = config.baseRam + players * config.ramPerPlayer;
    const ramLabel = Number.isInteger(exactRam) ? `${exactRam}` : exactRam.toFixed(1);

    useEffect(() => {
        onPlanChange?.(exactRam);
    }, [exactRam, onPlanChange]);

    return (
        <div className="w-full flex flex-col items-start text-left gap-2 relative">
            <h2 className="text-3xl font-bold font-akira relative z-10 uppercase tracking-wide bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-purple-400">
                Pricing Calculator
            </h2>
            <p className="text-white/60 text-sm mb-4 relative z-10 font-poppins">
                We need a few details about your game installation in order to recommend the best plan for you.
            </p>

            <div className="w-full bg-[#0B0C10]/60 border border-purple-500/20 rounded-xl p-5 md:p-6 flex flex-col gap-6 relative z-10 backdrop-blur-md shadow-[0_0_30px_-10px_rgba(168,85,247,0.15)] overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 blur-[80px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/4"></div>
                
                <div className="flex items-center gap-3 relative z-10">
                    <div className="p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 shrink-0 shadow-[0_0_15px_rgba(168,85,247,0.1)]">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                    </div>
                    <span className="text-sm font-medium text-white/90">How many players do you expect to be on the server at once?</span>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className="flex-1 w-full relative pt-2">
                        <input
                            type="range"
                            min={config.min}
                            max={config.max}
                            step={config.step}
                            value={players}
                            onChange={(e) => setPlayers(Number(e.target.value))}
                            className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-purple-500 hover:accent-purple-400 transition-all outline-none"
                            style={{
                                background: `linear-gradient(to right, #a855f7 ${((players - config.min) / (config.max - config.min)) * 100}%, rgba(255,255,255,0.1) ${((players - config.min) / (config.max - config.min)) * 100}%)`
                            }}
                        />
                    </div>
                    
                    <div className="flex items-center gap-2 bg-[#0B0C10]/80 border border-purple-500/30 rounded-lg px-4 py-2 shrink-0 min-w-[120px] justify-center shadow-[inset_0_0_15px_rgba(168,85,247,0.05)]">
                        <span className="text-purple-300 font-bold text-lg">{players}</span>
                        <span className="text-purple-300/60 text-sm font-medium">players</span>
                    </div>
                </div>

                {/*
                 * Show the estimate the recommendation is based on. Without this the
                 * calculator moved the plan carousel with no visible explanation, which
                 * read as the panel showing the wrong specs.
                 */}
                <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg px-4 py-3 bg-purple-500/5 border border-purple-500/20">
                    <span className="text-white/60 text-xs font-poppins">
                        Estimated memory for {players} {config.name} players
                    </span>
                    <span className="text-purple-300 font-semibold text-sm font-poppins">
                        ≈ {ramLabel} GB RAM
                    </span>
                </div>
            </div>
        </div>
    );
}
